import React, { useEffect, useState } from "react";
import { RotateDirection, SpecialZoomLevel } from "@react-pdf-viewer/core";
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

// Restricted PDF toolbar (no download / print / search) for non-admin roles.

const ZOOM_OPTIONS = [
  { value: SpecialZoomLevel.ActualSize, label: "Actual size" },
  { value: SpecialZoomLevel.PageFit, label: "Page fit" },
  { value: SpecialZoomLevel.PageWidth, label: "Page width" },
  ...[0.5, 0.75, 1, 1.25, 1.5, 2, 3].map((v) => ({
    value: v,
    label: `${v * 100}%`,
  })),
];

const iconButtonSx = { width: 32, height: 32 };

const ToolButton = ({ title, onClick, disabled = false, children }) => (
  <Tooltip title={title} arrow>
    {/* span keeps the tooltip working when the button is disabled */}
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        sx={iconButtonSx}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const PageInput = ({ currentPage, numberOfPages, jumpToPage }) => {
  const [value, setValue] = useState(String(currentPage + 1));

  useEffect(() => {
    setValue(String(currentPage + 1));
  }, [currentPage]);

  const commit = () => {
    const page = parseInt(value, 10);
    if (page >= 1 && page <= numberOfPages) {
      jumpToPage(page - 1);
    } else {
      setValue(String(currentPage + 1));
    }
  };

  return (
    <InputBase
      value={value}
      onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()}
      inputProps={{ "aria-label": "Page number", inputMode: "numeric" }}
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: 1,
        "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
        "&.Mui-focused": { borderColor: "primary.main" },
        "& input": {
          width: 36,
          padding: "4px 6px",
          fontSize: 14,
          textAlign: "center",
        },
      }}
    />
  );
};

const PdfViewerToolbar = ({ slots, jumpToPage }) => {
  const {
    CurrentPageLabel,
    GoToNextPage,
    GoToPreviousPage,
    Zoom,
    ZoomIn,
    ZoomOut,
    Rotate,
    EnterFullScreen,
  } = slots;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 1,
        minHeight: 48,
        px: 1.5,
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fff",
      }}
    >
      {/* Pages */}
      <GoToPreviousPage>
        {({ isDisabled, onClick }) => (
          <ToolButton title="Previous page" onClick={onClick} disabled={isDisabled}>
            <KeyboardArrowUpIcon fontSize="small" />
          </ToolButton>
        )}
      </GoToPreviousPage>

      <CurrentPageLabel>
        {({ currentPage, numberOfPages }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PageInput
              currentPage={currentPage}
              numberOfPages={numberOfPages}
              jumpToPage={jumpToPage}
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              / {numberOfPages}
            </Typography>
          </Box>
        )}
      </CurrentPageLabel>

      <GoToNextPage>
        {({ isDisabled, onClick }) => (
          <ToolButton title="Next page" onClick={onClick} disabled={isDisabled}>
            <KeyboardArrowDownIcon fontSize="small" />
          </ToolButton>
        )}
      </GoToNextPage>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Zoom */}
      <ZoomOut>
        {({ onClick }) => (
          <ToolButton title="Zoom out" onClick={onClick}>
            <ZoomOutIcon fontSize="small" />
          </ToolButton>
        )}
      </ZoomOut>

      <Zoom>
        {({ scale, onZoom }) => {
          const match = ZOOM_OPTIONS.find(
            (o) => typeof o.value === "number" && Math.abs(o.value - scale) < 0.001,
          );
          return (
            <Select
              size="small"
              value={match ? match.value : ""}
              displayEmpty
              renderValue={() => `${Math.round(scale * 100)}%`}
              onChange={(e) => onZoom(e.target.value)}
              inputProps={{ "aria-label": "Zoom level" }}
              sx={{
                minWidth: 88,
                fontSize: 14,
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              {ZOOM_OPTIONS.map((o) => (
                <MenuItem key={o.label} value={o.value} sx={{ fontSize: 14 }}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          );
        }}
      </Zoom>

      <ZoomIn>
        {({ onClick }) => (
          <ToolButton title="Zoom in" onClick={onClick}>
            <ZoomInIcon fontSize="small" />
          </ToolButton>
        )}
      </ZoomIn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* View */}
      <Rotate direction={RotateDirection.Forward}>
        {({ onClick }) => (
          <ToolButton title="Rotate clockwise" onClick={onClick}>
            <RotateRightIcon fontSize="small" />
          </ToolButton>
        )}
      </Rotate>

      <EnterFullScreen>
        {({ onClick }) => (
          <ToolButton title="Full screen" onClick={onClick}>
            <FullscreenIcon fontSize="small" />
          </ToolButton>
        )}
      </EnterFullScreen>
    </Box>
  );
};

export default PdfViewerToolbar;
